import { useMemo, useState } from 'react';
import {
  Copy,
  GripVertical,
  ImageOff,
  Pencil,
  Plus,
  Trash2,
  UtensilsCrossed,
} from 'lucide-react';
import type { Product, ProductCategory } from '@kick/types';
import { formatCOP } from '@/lib/cn';
import { cn } from '@/lib/cn';
import { Link } from 'react-router-dom';
import { getErrorMessage } from '@/lib/api';
import {
  useCrearSeccion,
  useDisponibilidadProducto,
  useDuplicarProducto,
  useEliminarProducto,
  useEliminarSeccion,
  useMerchantId,
  useProductos,
  useReordenarSecciones,
  useSecciones,
} from '@/lib/queries';
import {
  Boton,
  Campo,
  Cargando,
  ConfirmarModal,
  Entrada,
  ErrorCarga,
  Interruptor,
  Modal,
  Vacio
} from '@/components/ui';
import { useAvisos } from '@/components/avisos';
import { FormularioProducto } from './FormularioProducto';

export function Menu({ merchantId: deLaUrl }: { merchantId?: string } = {}) {
  const merchantId = useMerchantId(deLaUrl);
  const avisos = useAvisos();

  const secciones = useSecciones(merchantId);
  const productos = useProductos(merchantId);

  const [seccionNueva, setSeccionNueva] = useState(false);
  const [productoEditando, setProductoEditando] = useState<{ id?: string; categoryId: string } | null>(
    null,
  );
  const [porEliminar, setPorEliminar] = useState<Product | null>(null);
  const [seccionPorEliminar, setSeccionPorEliminar] = useState<ProductCategory | null>(null);

  const eliminarProducto = useEliminarProducto(merchantId);
  const eliminarSeccion = useEliminarSeccion(merchantId);
  const duplicar = useDuplicarProducto(merchantId);

  /** Productos agrupados por sección, en el orden en que se muestran. */
  const porSeccion = useMemo(() => {
    const mapa = new Map<string, Product[]>();
    for (const producto of productos.data ?? []) {
      const lista = mapa.get(producto.categoryId) ?? [];
      lista.push(producto);
      mapa.set(producto.categoryId, lista);
    }
    return mapa;
  }, [productos.data]);

  if (secciones.isLoading || productos.isLoading) return <Cargando texto="Cargando el menú…" />;

  if (secciones.isError) {
    return (
      <ErrorCarga mensaje={getErrorMessage(secciones.error)} onReintentar={() => secciones.refetch()} />
    );
  }

  const totalProductos = productos.data?.length ?? 0;
  const agotados = productos.data?.filter((p) => !p.isAvailable).length ?? 0;

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          {deLaUrl && (
            <Link to={`/comercios/${merchantId}`} className="text-sm text-kick-gray hover:text-kick-ink">
              ← Ficha del comercio
            </Link>
          )}
          <h1 className="text-3xl text-kick-dark">Menú</h1>
          <p className="mt-1 text-sm text-kick-gray">
            {totalProductos} producto{totalProductos === 1 ? '' : 's'} en{' '}
            {secciones.data?.length ?? 0} secci{secciones.data?.length === 1 ? 'ón' : 'ones'}
            {agotados > 0 && <span className="text-amber-600"> · {agotados} agotado(s)</span>}
          </p>
        </div>

        <Boton icono={<Plus className="h-4 w-4" />} onClick={() => setSeccionNueva(true)}>
          Nueva sección
        </Boton>
      </div>

      {secciones.data?.length === 0 ? (
        <div className="mt-8">
          <Vacio
            icono={<UtensilsCrossed className="h-6 w-6" />}
            titulo="Tu menú está vacío"
            descripcion="Empieza creando una sección — por ejemplo “Platos fuertes” o “Bebidas” — y agrega productos dentro."
            accion={
              <Boton icono={<Plus className="h-4 w-4" />} onClick={() => setSeccionNueva(true)}>
                Crear la primera sección
              </Boton>
            }
          />
        </div>
      ) : (
        <ListaSecciones
          merchantId={merchantId}
          secciones={secciones.data ?? []}
          porSeccion={porSeccion}
          onNuevoProducto={(categoryId) => setProductoEditando({ categoryId })}
          onEditarProducto={(producto) =>
            setProductoEditando({ id: producto.id, categoryId: producto.categoryId })
          }
          onDuplicar={(producto) =>
            duplicar.mutate(producto.id, {
              onSuccess: () => avisos.exito('Producto duplicado. La copia está oculta.'),
              onError: (e) => avisos.error(getErrorMessage(e)),
            })
          }
          onEliminarProducto={setPorEliminar}
          onEliminarSeccion={setSeccionPorEliminar}
        />
      )}

      <ModalSeccion
        abierto={seccionNueva}
        merchantId={merchantId}
        siguienteOrden={secciones.data?.length ?? 0}
        onCerrar={() => setSeccionNueva(false)}
      />

      {productoEditando && (
        <FormularioProducto
          merchantId={merchantId}
          productoId={productoEditando.id}
          categoryIdInicial={productoEditando.categoryId}
          secciones={secciones.data ?? []}
          onCerrar={() => setProductoEditando(null)}
        />
      )}

      <ConfirmarModal
        abierto={!!porEliminar}
        titulo="Eliminar producto"
        mensaje={`¿Seguro que quieres eliminar “${porEliminar?.name}”? Dejará de aparecer en la tienda, pero los pedidos anteriores lo seguirán mostrando.`}
        cargando={eliminarProducto.isPending}
        onCancelar={() => setPorEliminar(null)}
        onConfirmar={() =>
          porEliminar &&
          eliminarProducto.mutate(porEliminar.id, {
            onSuccess: () => {
              avisos.exito('Producto eliminado');
              setPorEliminar(null);
            },
            onError: (e) => avisos.error(getErrorMessage(e)),
          })
        }
      />

      <ConfirmarModal
        abierto={!!seccionPorEliminar}
        titulo="Eliminar sección"
        mensaje={`¿Eliminar la sección “${seccionPorEliminar?.name}”?`}
        cargando={eliminarSeccion.isPending}
        onCancelar={() => setSeccionPorEliminar(null)}
        onConfirmar={() =>
          seccionPorEliminar &&
          eliminarSeccion.mutate(seccionPorEliminar.id, {
            onSuccess: () => {
              avisos.exito('Sección eliminada');
              setSeccionPorEliminar(null);
            },
            onError: (e) => avisos.error(getErrorMessage(e)),
          })
        }
      />
    </div>
  );
}

// ── Lista de secciones con reordenamiento ───────────────────

function ListaSecciones({
  merchantId,
  secciones,
  porSeccion,
  onNuevoProducto,
  onEditarProducto,
  onDuplicar,
  onEliminarProducto,
  onEliminarSeccion,
}: {
  merchantId: string;
  secciones: ProductCategory[];
  porSeccion: Map<string, Product[]>;
  onNuevoProducto: (categoryId: string) => void;
  onEditarProducto: (producto: Product) => void;
  onDuplicar: (producto: Product) => void;
  onEliminarProducto: (producto: Product) => void;
  onEliminarSeccion: (seccion: ProductCategory) => void;
}) {
  const reordenar = useReordenarSecciones(merchantId);
  const [arrastrando, setArrastrando] = useState<string | null>(null);

  /** Arrastrar y soltar con la API nativa del navegador, sin dependencias. */
  const alSoltar = (destinoId: string): void => {
    if (!arrastrando || arrastrando === destinoId) return;

    const ids = secciones.map((s) => s.id);
    const desde = ids.indexOf(arrastrando);
    const hasta = ids.indexOf(destinoId);
    ids.splice(hasta, 0, ids.splice(desde, 1)[0]!);

    reordenar.mutate(ids);
    setArrastrando(null);
  };

  return (
    <div className="mt-6 space-y-4">
      {secciones.map((seccion) => {
        const productos = porSeccion.get(seccion.id) ?? [];

        return (
          <section
            key={seccion.id}
            draggable
            onDragStart={() => setArrastrando(seccion.id)}
            onDragEnd={() => setArrastrando(null)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => alSoltar(seccion.id)}
            className={cn(
              'rounded-kick bg-white shadow-card transition',
              arrastrando === seccion.id && 'opacity-40',
            )}
          >
            <header className="flex items-center gap-2 border-b border-kick-gray/15 px-4 py-3">
              <GripVertical
                className="h-5 w-5 cursor-grab text-kick-gray/40 active:cursor-grabbing"
                aria-label="Arrastrar para reordenar"
              />
              <h2 className="text-lg text-kick-dark">{seccion.name}</h2>
              <span className="rounded-full bg-kick-light px-2 py-0.5 text-xs font-semibold text-kick-gray">
                {productos.length}
              </span>

              <div className="ml-auto flex items-center gap-1">
                <Boton
                  variante="fantasma"
                  className="px-2 py-1.5"
                  icono={<Plus className="h-4 w-4" />}
                  onClick={() => onNuevoProducto(seccion.id)}
                >
                  <span className="hidden sm:inline">Producto</span>
                </Boton>
                <button
                  onClick={() => onEliminarSeccion(seccion)}
                  aria-label={`Eliminar sección ${seccion.name}`}
                  className="rounded-kick p-2 text-kick-gray transition hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </header>

            {productos.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-kick-gray">
                Sin productos todavía.
              </p>
            ) : (
              <ul className="divide-y divide-kick-gray/10">
                {productos.map((producto) => (
                  <FilaProducto
                    key={producto.id}
                    merchantId={merchantId}
                    producto={producto}
                    onEditar={() => onEditarProducto(producto)}
                    onDuplicar={() => onDuplicar(producto)}
                    onEliminar={() => onEliminarProducto(producto)}
                  />
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

function FilaProducto({
  merchantId,
  producto,
  onEditar,
  onDuplicar,
  onEliminar,
}: {
  merchantId: string;
  producto: Product;
  onEditar: () => void;
  onDuplicar: () => void;
  onEliminar: () => void;
}) {
  const disponibilidad = useDisponibilidadProducto(merchantId);
  const avisos = useAvisos();

  return (
    <li
      className={cn(
        'flex items-center gap-3 px-4 py-3 transition hover:bg-kick-light/50',
        !producto.isAvailable && 'bg-kick-light/40',
      )}
    >
      {producto.imageUrl ? (
        <img
          src={producto.imageUrl}
          alt=""
          loading="lazy"
          className={cn(
            'h-14 w-14 shrink-0 rounded-kick bg-kick-light object-contain',
            !producto.isAvailable && 'grayscale',
          )}
        />
      ) : (
        <div className="grid h-14 w-14 shrink-0 place-items-center rounded-kick bg-kick-light text-kick-gray/50">
          <ImageOff className="h-5 w-5" />
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p
            className={cn(
              'truncate font-semibold text-kick-ink',
              !producto.isAvailable && 'text-kick-gray',
            )}
          >
            {producto.name}
          </p>
          {!producto.isAvailable && (
            <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
              Agotado
            </span>
          )}
        </div>
        {producto.description && (
          <p className="truncate text-sm text-kick-gray">{producto.description}</p>
        )}
      </div>

      <div className="shrink-0 text-right">
        <p className="font-semibold text-kick-ink">{formatCOP(producto.price)}</p>
        {producto.compareAtPrice && (
          <p className="text-xs text-kick-gray line-through">
            {formatCOP(producto.compareAtPrice)}
          </p>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1 pl-2">
        <Interruptor
          activo={producto.isAvailable}
          etiqueta={`Disponibilidad de ${producto.name}`}
          onCambio={(disponible) =>
            disponibilidad.mutate(
              { id: producto.id, isAvailable: disponible },
              { onError: (e) => avisos.error(getErrorMessage(e)) },
            )
          }
        />
        <button
          onClick={onEditar}
          aria-label={`Editar ${producto.name}`}
          className="rounded-kick p-2 text-kick-gray transition hover:bg-white hover:text-kick-ink"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          onClick={onDuplicar}
          aria-label={`Duplicar ${producto.name}`}
          className="hidden rounded-kick p-2 text-kick-gray transition hover:bg-white hover:text-kick-ink sm:block"
        >
          <Copy className="h-4 w-4" />
        </button>
        <button
          onClick={onEliminar}
          aria-label={`Eliminar ${producto.name}`}
          className="rounded-kick p-2 text-kick-gray transition hover:bg-red-50 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}

// ── Modal de sección ────────────────────────────────────────

function ModalSeccion({
  abierto,
  merchantId,
  siguienteOrden,
  onCerrar,
}: {
  abierto: boolean;
  merchantId: string;
  siguienteOrden: number;
  onCerrar: () => void;
}) {
  const [nombre, setNombre] = useState('');
  const crear = useCrearSeccion(merchantId);
  const avisos = useAvisos();

  const enviar = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!nombre.trim()) return;

    crear.mutate(
      { name: nombre.trim(), sortOrder: siguienteOrden },
      {
        onSuccess: () => {
          avisos.exito('Sección creada');
          setNombre('');
          onCerrar();
        },
        onError: (error) => avisos.error(getErrorMessage(error)),
      },
    );
  };

  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo="Nueva sección"
      descripcion="Las secciones agrupan tu menú: Entradas, Platos fuertes, Bebidas…"
      ancho="sm"
    >
      <form onSubmit={enviar} className="p-5">
        <Campo etiqueta="Nombre de la sección" requerido>
          <Entrada
            autoFocus
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Platos fuertes"
            maxLength={100}
          />
        </Campo>

        <div className="mt-6 flex justify-end gap-2">
          <Boton type="button" variante="secundario" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton type="submit" cargando={crear.isPending} disabled={!nombre.trim()}>
            Crear sección
          </Boton>
        </div>
      </form>
    </Modal>
  );
}
